<template>
  <div class="ayp-result-page min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 加载状态 -->
    <div v-if="loading" class="fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50">
      <div class="text-center">
        <span class="text-6xl animate-pulse">💖</span>
        <p class="mt-4 text-lg text-gray-600">正在加载您的结果...</p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50">
      <div class="card p-8 text-center max-w-md mx-4">
        <span class="text-6xl">😢</span>
        <h2 class="text-2xl font-bold text-gray-900 mt-4">加载失败</h2>
        <p class="text-gray-600 mt-2">{{ error }}</p>
        <button @click="$router.push('/')" class="btn-primary mt-6">返回首页</button>
      </div>
    </div>

    <!-- 结果展示 -->
    <div v-else>
      <!-- 顶部导航 -->
      <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div class="container mx-auto px-4 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <router-link to="/" class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                  <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                </svg>
              </router-link>
              <div class="flex items-center gap-2">
                <span class="text-2xl">💕</span>
                <span class="font-semibold text-psychology-primary">年上年下恋爱倾向测试</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <!-- 主内容区 -->
      <main class="container mx-auto px-4 py-8">
        <div class="max-w-4xl mx-auto space-y-6">
          <!-- 1. AYP指数展示 -->
          <AYPScoreDisplay 
            :score="result.aypScore"
          />

          <!-- 2. 结果解释 -->
          <ResultExplanation 
            :ayp-score="result.aypScore"
            :scores="result.scores"
          />

          <!-- 3. 四个维度得分能量条 -->
          <DimensionScoresBars 
            :scores="result.scores"
          />

          <!-- 4. 年上年下恋爱人格类型分析 -->
          <PersonalityTypeCard 
            :type-name="result.personalityType"
          />

          <!-- 5. 依恋类型人格分析 -->
          <AttachmentTypeCard 
            :primary-type="result.attachmentType.primary"
            :secondary-type="result.attachmentType.secondary"
            :primary-score="result.attachmentType.primaryScore"
            :secondary-score="result.attachmentType.secondaryScore"
          />

          <!-- 6. 个性化建议 -->
          <PersonalizedAdvice 
            :ayp-score="result.aypScore"
          />

          <!-- 7. 可视化分析区域 -->
          <div class="space-y-6">
          <!-- 四维度雷达图 -->
          <FourDimensionRadar 
            :scores="result.scores"
          />

            <!-- 依恋类型雷达图 -->
            <AttachmentRadarChart 
              :scores="result.scores"
            />

            <!-- 路径分析图 -->
            <PathAnalysis 
              :scores="result.scores"
              :ayp-score="result.aypScore"
            :attachment-type="result.attachmentType"
          />
          </div>

          <!-- 8. 星座分析（如果有） -->
          <ZodiacAnalysis 
            v-if="result.userInfo && result.userInfo.zodiac"
            :zodiac="result.userInfo.zodiac"
          />

          <!-- 9. 专业心理学解读 -->
          <ProfessionalInterpretation 
            :ayp-score="result.aypScore"
            :attachment-type="result.attachmentType"
          />

          <!-- 操作按钮 -->
          <div class="flex flex-col sm:flex-row gap-3 pt-4">
            <!-- <button @click="captureAndShare" class="btn-secondary flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" x2="12" y1="15" y2="3"></line>
              </svg>
              保存结果
            </button> -->
            <button @click="retakeTest" class="btn-primary flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
              </svg>
              重新测试
            </button>
          </div>

          <!-- 测试信息 -->
          <div class="card p-4 bg-gray-50">
            <div class="flex items-center justify-between text-sm text-gray-600">
              <div class="flex items-center gap-4">
                <span>📊 测试类型: {{ testTypeText }}</span>
                <span v-if="result.userInfo && result.userInfo.age">👤 年龄段: {{ result.userInfo.age }}</span>
              </div>
              <span>⏰ {{ formatDate(result.endTime) }}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { getSessionById, getAllSessions } from '@/utils/sessionStorage'
import { useToast } from 'vue-toastification'
import html2canvas from 'html2canvas'
import AYPScoreDisplay from '@/components/result/AYPScoreDisplay.vue'
import ResultExplanation from '@/components/result/ResultExplanation.vue'
import DimensionScoresBars from '@/components/result/DimensionScoresBars.vue'
import PersonalityTypeCard from '@/components/result/PersonalityTypeCard.vue'
import AttachmentTypeCard from '@/components/result/AttachmentTypeCard.vue'
import PersonalizedAdvice from '@/components/result/PersonalizedAdvice.vue'
import FourDimensionRadar from '@/components/result/FourDimensionRadar.vue'
import AttachmentRadarChart from '@/components/result/AttachmentRadarChart.vue'
import PathAnalysis from '@/components/result/PathAnalysis.vue'
import ZodiacAnalysis from '@/components/result/ZodiacAnalysis.vue'
import ProfessionalInterpretation from '@/components/result/ProfessionalInterpretation.vue'

export default {
  name: 'AYPResultPage',
  components: {
    AYPScoreDisplay,
    ResultExplanation,
    DimensionScoresBars,
    PersonalityTypeCard,
    AttachmentTypeCard,
    PersonalizedAdvice,
    FourDimensionRadar,
    AttachmentRadarChart,
    PathAnalysis,
    ZodiacAnalysis,
    ProfessionalInterpretation
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      loading: true,
      error: null,
      result: null,
      sessionId: null
    }
  },
  computed: {
    testTypeText() {
      const typeMap = {
        'self': '给自己测(新版)',
        'partner': '为恋人测(新版)',
        'self_new': '给自己测(原版)',
        'partner_new': '为恋人测(原版)'
      }
      return typeMap[this.result?.testType] || '未知类型'
    }
  },
  mounted() {
    this.loadResult()
  },
  methods: {
    async loadResult() {
      try {
        this.loading = true
        
        // 通过 sessionId 获取会话
        this.sessionId = this.$route.query.sessionId

        if (!this.sessionId) {
          throw new Error('缺少会话ID')
        }

        console.log('查找会话，sessionId:', this.sessionId, '类型:', typeof this.sessionId)
        
        // 调试：查看所有会话
        const allData = getAllSessions()
        console.log('所有会话数量:', allData.sessions.length)
        if (allData.sessions.length > 0) {
          console.log('所有会话ID:', allData.sessions.map(s => ({ id: s.id, type: typeof s.id, completed: s.completed })))
        }

        const session = getSessionById(this.sessionId)
        if (!session) {
          console.error('未找到会话，查找的ID:', this.sessionId, '类型:', typeof this.sessionId)
          throw new Error('找不到测试结果')
        }

        if (!session.completed) {
          throw new Error('测试未完成')
        }

        // 获取测试结果，合并session和results中的数据
        if (!session.results) {
          throw new Error('测试结果数据不完整')
        }

        // 合并session和results中的数据
        // endTime和testType在session对象上，其他数据在session.results中
        this.result = {
          ...session.results,
          endTime: session.endTime || session.results.endTime,
          testType: session.testType || session.results.testType,
          userInfo: session.userInfo || session.results.userInfo
        }

        // 验证必要字段
        if (!this.result.scores) {
          throw new Error('测试结果缺少分数数据')
        }
        if (!this.result.endTime) {
          throw new Error('测试结果缺少结束时间')
        }

        console.log('加载AYP结果:', this.result)

      } catch (err) {
        console.error('加载结果失败:', err)
        this.error = err.message
        this.toast.error(err.message)
      } finally {
        this.loading = false
      }
    },
    async captureAndShare() {
      try {
        this.toast.info('正在生成图片...')
        
        // 等待一小段时间确保页面完全渲染
        await new Promise(resolve => setTimeout(resolve, 300))
        
        const element = document.querySelector('.ayp-result-page main')
        if (!element) {
          throw new Error('无法找到结果内容')
        }

        // 辅助函数：在克隆文档中修复 color-mix 问题
        const fixColorMix = (clonedDoc, originalElement) => {
          // 获取原始元素的所有子元素
          const originalElements = originalElement.querySelectorAll('*')
          const clonedElements = clonedDoc.querySelectorAll('*')
          
          // 为每个克隆的元素应用原始元素的计算样式
          originalElements.forEach((originalEl, index) => {
            if (clonedElements[index]) {
              const clonedEl = clonedElements[index]
              const computedStyle = window.getComputedStyle(originalEl)
              
              // 应用计算后的样式值，这些值已经解析了 color-mix
              clonedEl.style.backgroundColor = computedStyle.backgroundColor
              clonedEl.style.color = computedStyle.color
              clonedEl.style.borderColor = computedStyle.borderColor
              clonedEl.style.borderTopColor = computedStyle.borderTopColor
              clonedEl.style.borderRightColor = computedStyle.borderRightColor
              clonedEl.style.borderBottomColor = computedStyle.borderBottomColor
              clonedEl.style.borderLeftColor = computedStyle.borderLeftColor
              
              // 处理背景渐变（如果有）
              const bgImage = computedStyle.backgroundImage
              if (bgImage && bgImage !== 'none') {
                clonedEl.style.backgroundImage = bgImage
              }
            }
          })
          
          // 处理主元素本身
          const clonedMain = clonedDoc.querySelector('main')
          if (clonedMain) {
            const mainComputedStyle = window.getComputedStyle(element)
            clonedMain.style.backgroundColor = mainComputedStyle.backgroundColor
            clonedMain.style.backgroundImage = mainComputedStyle.backgroundImage || 'none'
          }
        }

        // 使用 html2canvas 生成图片
        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          onclone: (clonedDoc) => {
            // 在克隆的文档中修复 color-mix 问题
            fixColorMix(clonedDoc, element)
          },
          // 忽略不支持的 CSS 函数，使用计算后的样式
          ignoreElements: (element) => {
            // 不忽略任何元素，让 onclone 处理样式
            console.log(element)
            return false
          }
        })

        // 将 canvas 转换为 blob
        return new Promise((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('图片生成失败'))
              return
            }
            
            try {
              // 创建下载链接
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.download = `AYP测试结果_${new Date().getTime()}.png`
              link.href = url
              
              // 触发下载
              document.body.appendChild(link)
              link.click()
              
              // 清理
              document.body.removeChild(link)
              URL.revokeObjectURL(url)
              
              this.toast.success('结果已保存到本地')
              resolve()
            } catch (err) {
              reject(err)
            }
          }, 'image/png', 0.95)
        })
      } catch (err) {
        console.error('保存失败:', err)
        this.toast.error(`保存失败: ${err.message || '请重试'}`)
      }
    },
    retakeTest() {
      this.$router.push('/assessment/select-type')
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.ayp-result-page {
  min-height: 100vh;
}
</style>

